import { Link } from "react-router-dom";

/*
 * Blog article: "Understanding Your Testosterone Dose Response"
 * Rendered inside BlogPost.jsx via <article className="legal-prose">.
 */
export default function ArticleBody() {
    return (
        <>
            <h2>Quick answer</h2>
            <p>
                Two men on the same weekly dose rarely land on the same
                blood level. One hits mid-range on 100 mg a week. Another
                doesn't hit optimization territory until 175 mg. Your
                responder type is which of those you are: how much
                testosterone shows up in your bloodwork for the drug you
                actually take. TRT Plus works out your precise responder
                type inside your AI bloodwork report.
            </p>

            <h2>Comparing lab numbers</h2>
            <p>
                Interpreting that single Total T number on a blood test is
                more complex than it sounds. Three variables move a blood
                level, before physiology even enters into it.
            </p>
            <p>
                <strong>Draw timing.</strong> A peak draw and a trough draw
                are two separate measurements of the same protocol, and
                the gap between them is ridiculous.
            </p>
            <p>
                <strong>Injection frequency.</strong> How often you inject
                sets how far your level swings between peak and trough.
                The same weekly total, split into daily or every-other-day
                shots, holds a steadier level than one or two injections a
                week. The drug hasn't changed. Only the shape of the
                curve.
            </p>
            <p>
                <strong>The ester.</strong> Cypionate, enanthate, and
                propionate are all testosterone with a different chemical
                tail attached, and that tail changes two things. First,
                the release speed: propionate's half-life is about a day,
                enanthate's four to five, cypionate's eight. Varying
                half-lives produce different curve shapes, which change
                relative levels. Second, the testosterone you get by
                weight: cypionate is about 70%, enanthate 72%, propionate
                84%. So 100 mg of cyp delivers only 70 mg of actual
                testosterone while 100 mg of prop delivers 84 mg. Equal
                numbers on two vials are not equal mg doses.
            </p>

            <h2>Comparing like for like</h2>
            <p>
                Before your reading can mean anything, it has to be put on
                a common basis, with the draw timing, the ester, and the
                frequency all accounted for. That normalized reading is
                then compared against dose-response data from published
                trials, and returns a responder type.
            </p>
            <p>
                Reading your dose response this way should be the basic
                entry point in judging protocol effectiveness. Even in a
                clinical setting, bloodwork is often reviewed in complete
                isolation from the protocol that produced it. The number
                gets assessed while the ester, dose, frequency, and draw
                timing behind it go unmentioned.
            </p>

            <h2>The six responder types</h2>
            <p>
                Super-Responder, Hyper-Responder, Normal Responder, Low
                Responder, Partial Responder, Non-Responder.
            </p>
            <p>
                In plain terms, hyper and super responders are cheap
                dates. They reach high testosterone on a relatively small
                dose. Partial and non-responders need more to reach the
                same target. Most men land in the normal responder group.
            </p>

            <h2>What the evidence shows</h2>
            <p>
                The dose-response curve is well established. The anchor is
                Bhasin's 2001 trial (
                <a
                    href="https://doi.org/10.1152/ajpendo.2001.281.6.E1172"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Bhasin et al., 2001
                </a>
                ): 61 healthy young men, each given a fixed weekly dose of
                testosterone enanthate for 20 weeks. Here is what came
                back:
            </p>

            <div className="my-8 overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-sm">
                    <thead className="bg-white/5">
                        <tr>
                            <th className="text-left px-4 py-3 font-mono uppercase text-xs tracking-widest text-white/70">Weekly dose</th>
                            <th className="text-left px-4 py-3 font-mono uppercase text-xs tracking-widest text-white/70">Trough testosterone</th>
                            <th className="text-left px-4 py-3 font-mono uppercase text-xs tracking-widest text-white/70">Lean mass change (20 wk)</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                        <tr><td className="px-4 py-3">25 mg</td><td className="px-4 py-3">253 ng/dL<br/>8.8 nmol/L</td><td className="px-4 py-3">−2.2 lb<br/>−1.0 kg</td></tr>
                        <tr><td className="px-4 py-3">50 mg</td><td className="px-4 py-3">306 ng/dL<br/>10.6 nmol/L</td><td className="px-4 py-3">+1.3 lb<br/>+0.6 kg</td></tr>
                        <tr><td className="px-4 py-3">125 mg</td><td className="px-4 py-3">542 ng/dL<br/>18.8 nmol/L</td><td className="px-4 py-3">+7.5 lb<br/>+3.4 kg</td></tr>
                        <tr><td className="px-4 py-3">300 mg</td><td className="px-4 py-3">1,345 ng/dL<br/>46.6 nmol/L</td><td className="px-4 py-3">+11.5 lb<br/>+5.2 kg</td></tr>
                        <tr><td className="px-4 py-3">600 mg</td><td className="px-4 py-3">2,370 ng/dL<br/>82.2 nmol/L</td><td className="px-4 py-3">+17.4 lb<br/>+7.9 kg</td></tr>
                    </tbody>
                </table>
            </div>

            <p>
                The low doses left men worse off. At 25 mg a week, men who
                started out perfectly normal finished with a trough of 253
                ng/dL, hypogonadal territory, and they lost lean mass. 50
                mg barely broke even. A replacement dose has to exceed
                your natural output before it is replacing anything.
            </p>
            <p>
                Entering supra territory, the returns diminish hard. Going
                from 125 mg to 600 mg is nearly five times the drug for
                about double the lean mass.
            </p>
            <p>
                The pattern repeats elsewhere. Older men gain muscle in
                proportion to dose much as young men do (
                <a
                    href="https://doi.org/10.1210/jc.2004-1184"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Bhasin et al., 2005
                </a>
                ). And it isn't only muscle that tracks the dose:
                hemoglobin and hematocrit climb with it too, and more
                steeply in older men (
                <a
                    href="https://doi.org/10.1210/jc.2007-1692"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Coviello et al., 2008
                </a>
                ). More dose is more of everything, not only the part you
                want.
            </p>

            <h2>Creams are a different beast</h2>
            <p>
                Cream behaves nothing like an injection, so it is handled
                separately.
            </p>
            <p>
                It is applied daily, so frequency isn't a variable. But
                absorption kinetics are site-dependent and saturating:
                scrotal skin absorbs several times more than a shoulder or
                a thigh, and because absorption saturates, doubling the
                applied dose does not double what gets in. The profile is
                peaky, rising within about two hours and falling by
                sixteen at modest doses (
                <a
                    href="https://doi.org/10.1111/andr.12357"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Iyer et al., 2017
                </a>
                ), which makes the gap between a morning and an evening
                draw much larger than on a frequent injectable. Scrotal
                skin is also rich in 5-alpha reductase, so it drives a
                disproportionate DHT response.
            </p>
            <p>
                On cream, which site you use matters as much as how much
                you apply, and draw timing is more volatile. A peak is
                targeted two to three hours after application, a trough
                with a draw just before your due application. TRT Plus
                expresses an applied cream dose as an injectable-equivalent
                figure for your chosen site, through its{" "}
                <Link to="/engine">transdermal saturation model</Link>, so
                a cream protocol can be compared to an injection protocol
                mg for mg, accounting for all variables and conversions
                discussed thus far.
            </p>

            <h2>Read in context</h2>
            <p>
                A raw number tells you almost nothing without the protocol
                that produced it. Putting it on a common basis is what
                makes it readable, and that is the whole point of a
                responder type. The TRT Plus engine assesses your
                bloodwork in the context of the protocol that produced
                it, so the deterministic analysis and any recommendations
                are made within a consistent framework.
            </p>

            <h2>Frequently asked questions</h2>

            <h3>Can I compare my level to another guy's on the same dose?</h3>
            <p>
                A different ester, a different injection frequency, and a
                different draw time mean you are comparing different
                quantities. Both readings have to be put on a common basis
                first, which is exactly what a responder type does.
            </p>

            <h3>My responder type came back low. Do I need more testosterone?</h3>
            <p>
                A low responder type does not mean your protocol needs
                adjusting. It just means you needed more testosterone than
                an efficient responder to get into the TRT-calibrated
                zone, and if you are there, you are fine. If your level
                itself is below target, rule out under-dosed product, poor
                injection technique, and an unsettled protocol before
                adding milligrams.
            </p>

            <div className="hairline my-10" />

            <p className="text-white/50 text-sm italic">
                This article is for informational purposes only and does not
                constitute medical advice. Consult with your healthcare
                provider before making medical decisions.
            </p>

            <h3>See it in the app</h3>
            <p>
                TRT Plus works out your responder type from a single blood
                test and your protocol, cream or injection.{" "}
                <Link to="/engine">See the engine</Link> ·{" "}
                <Link to="/">Get TRT Plus</Link>.
            </p>

            <h3>Related reading</h3>
            <ul>
                <li>
                    <Link to="/blog/tracking-a-bad-protocol-is-still-a-bad-protocol">
                        Tracking a Bad Protocol Is Still a Bad Protocol
                    </Link>
                </li>
                <li>
                    <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                        How TRT Plus Analyzes Your Bloodwork: A
                        Deterministic Engine, Then AI
                    </Link>
                </li>
            </ul>
        </>
    );
}
